import { unwrap } from '@hive/api';
import { Icon, useLoad } from '@hive/ui';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useT, type PlatformKey } from '../i18n';
import { useSession } from '../lib/session';

const KEY = 'hive.guide.open';
const remembered = () => {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * "Como iniciar" na barra: o passo a passo como assistente lateral que fica aberto enquanto a pessoa navega.
 * Cada passo se marca sozinho pela evidência real (sites, agentes, cobertura, coletor, varreduras...) e leva à tela
 * onde se faz e à seção do manual.
 */
export function StartGuideButton() {
  const t = useT();
  const s = useSession();
  const loc = useLocation();
  const [open, setOpen] = useState(remembered);
  const guide = useLoad(() => unwrap(s.api.GET('/api/v1/onboarding/guide')).catch(() => null), [s.api]);
  // as seções do artigo no idioma da pessoa: a 1ª é "Antes de começar", as seguintes são os passos na mesma ordem
  const article = useLoad(() => unwrap(s.api.GET('/api/v1/help/articles/{id}', { params: { path: { id: 'start-guide' } } })).catch(() => null), [s.api]);
  const { reload } = guide;
  // a cada tela nova o progresso é relido (o passo pode ter acabado de ser feito)
  useEffect(() => {
    if (open) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, open]);
  // aberto, o guia ocupa a lateral e o conteúdo da tela encolhe para não ficar por baixo dele
  useEffect(() => {
    document.documentElement.classList.toggle('guide-open', open);
    return () => document.documentElement.classList.remove('guide-open');
  }, [open]);
  useEffect(() => {
    try {
      localStorage.setItem(KEY, open ? '1' : '0');
    } catch {
      /* navegador sem armazenamento: só não lembra */
    }
  }, [open]);
  const g = guide.data;
  // lugar do botão guardado enquanto o progresso carrega: os botões da barra não andam quando ele chega
  if (!g && guide.loading)
    return (
      <span className="icon-btn guide-btn tb-hide-sm" aria-hidden="true" style={{ visibility: 'hidden' }}>
        <Icon name="play" />
        <span className="guide-count">00/00</span>
      </span>
    );
  if (!g) return null;
  const next = g.steps?.find((x) => !x.done);
  return (
    <>
      <button
        type="button"
        className="icon-btn guide-btn tb-hide-sm"
        aria-expanded={open}
        aria-controls="start-guide"
        title={t('guide.open')}
        onClick={() => setOpen(!open)}
      >
        <Icon name="play" />
        <span className="guide-count">
          {g.done}/{g.total}
        </span>
      </button>
      {open && (
        <aside id="start-guide" className="guide-sheet" aria-label={t('guide.title')}>
          <div className="guide-head">
            <div>
              <b>{t('guide.title')}</b>
              <div className="small muted">{t('guide.progress', { done: g.done ?? 0, total: g.total ?? 0 })}</div>
            </div>
            <button type="button" className="icon-btn" aria-label={t('guide.close')} onClick={() => setOpen(false)}>
              <Icon name="x" />
            </button>
          </div>
          <div className="guide-bar" aria-hidden="true">
            <span style={{ width: `${((g.done ?? 0) * 100) / Math.max(1, g.total ?? 1)}%` }} />
          </div>
          <ol className="guide-steps">
            {(g.steps ?? []).map((st, i) => (
              <li key={st.key} className={st.done ? 'done' : st.key === next?.key ? 'next' : undefined}>
                <span className="guide-dot" aria-hidden="true">
                  {st.done ? '✓' : i + 1}
                </span>
                <div className="guide-body">
                  <b>{t(`guide.${st.key}.title` as PlatformKey)}</b>
                  <span className="sr-only">{st.done ? t('guide.done') : t('guide.todo')}</span>
                  <p className="small muted">{t(`guide.${st.key}.what` as PlatformKey, { n: st.count ?? 0 })}</p>
                  <div className="row small" style={{ gap: 12 }}>
                    <Link to={st.route ?? '/'}>{t('guide.go')}</Link>
                    <Link to={`/help/start-guide#${article.data?.headings?.[i + 1]?.anchor ?? ''}`}>{t('guide.manual')}</Link>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          <Link className="small" to="/help/start-guide">
            {t('guide.full')}
          </Link>
        </aside>
      )}
    </>
  );
}
